import { useState } from 'react';
import type { Dictionary } from '../i18n/dictionary';
import type { Story } from '../lib/story';
import { Checklist } from './Checklist';

const sample: Dictionary<{ ingredients: string[]; steps: string[] }> = {
  pl: {
    ingredients: [
      '500 ml zakwasu',
      '200 g białej kiełbasy',
      '2 ząbki czosnku',
      '1 łyżka majeranku',
    ],
    steps: [
      'Kiełbasę zalej litrem wody i gotuj 20 minut.',
      'Wyjmij kiełbasę, do wywaru wlej zakwas i dodaj przeciśnięty czosnek. Gotuj na małym ogniu jeszcze 10 minut, aż zupa lekko zgęstnieje, a na koniec dopraw majerankiem, solą i pieprzem.',
      'Podawaj z pokrojoną kiełbasą i jajkiem.',
    ],
  },
  en: {
    ingredients: [
      '500 ml sourdough starter',
      '200 g white sausage',
      '2 garlic cloves',
      '1 tbsp marjoram',
    ],
    steps: [
      'Cover the sausage with a litre of water and simmer for 20 minutes.',
      'Take the sausage out, pour the starter into the broth and add the pressed garlic. Simmer for another 10 minutes until the soup thickens slightly, then season with marjoram, salt and pepper.',
      'Serve with the sliced sausage and an egg.',
    ],
  },
};

/** The checklist owns no ticks; the screen around it does. */
function Demo({
  items,
  numbered,
  initial = [],
}: {
  items: string[];
  numbered?: boolean;
  initial?: number[];
}) {
  const [checked, setChecked] = useState(() =>
    items.map((_, i) => initial.includes(i)),
  );
  return (
    <Checklist
      items={items}
      checked={checked}
      numbered={numbered}
      onToggle={(i) =>
        setChecked((c) => c.map((on, j) => (j === i ? !on : on)))
      }
    />
  );
}

export default {
  kind: 'component',
  title: { pl: 'Lista do odhaczania', en: 'Checklist' },
  description: {
    pl: 'Pozycje do odhaczania po kolei, np. składniki i kroki przepisu. Odhaczona pozycja zostaje na miejscu, przygaszona i przekreślona. Cały wiersz jest etykietą pola wyboru, więc dotknięcie tekstu też przełącza. Wariant `numbered` numeruje pozycje od 1.',
    en: 'Items to tick off one by one, such as a recipe’s ingredients and steps. A ticked item stays in place, dimmed and struck through. The whole row is the checkbox’s label, so a tap on the text toggles it too. The `numbered` variant numbers items from 1.',
  },
  variants: [
    {
      name: { pl: 'Zwykła', en: 'Plain' },
      render: (locale) => <Demo items={sample[locale].ingredients} />,
    },
    {
      name: { pl: 'Numerowana', en: 'Numbered' },
      render: (locale) => <Demo items={sample[locale].steps} numbered />,
    },
    {
      name: { pl: 'Częściowo odhaczona', en: 'Partly ticked' },
      render: (locale) => (
        <Demo items={sample[locale].ingredients} initial={[0, 2]} />
      ),
    },
  ],
} satisfies Story;
